import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { type ChainflipToken } from '@/shared/assets/tokens';
import usePrevious from '@/shared/hooks/usePrevious';
import useValidatedTokenAmount from '@/shared/hooks/useValidatedTokenAmount';
import useValidatedTokenAmountInput from '@/shared/hooks/useValidatedTokenAmountInput';
import type TokenAmount from '@/shared/utils/TokenAmount';
import { SkeletonLine } from '../atoms/LoadingSkeleton';

export interface TokenInputAmountProps {
  token: ChainflipToken | undefined;
  value: TokenAmount | undefined;
  onChange: (value: TokenAmount | undefined) => void;
  balance?: TokenAmount;
  label?: string;
  error?: string;
  showError?: boolean;
  disabled?: boolean;
  disableMaxButton?: boolean;
  focusOnMount?: boolean;
  acceptOverflowAmount?: boolean;
  useBalanceAsMaxButton?: boolean;
  isBalanceLoading?: boolean;
  setError?: (value: string) => void;
  onClickMax?: VoidFunction;
  balanceLabel?: string;
  maxUpperLimit?: TokenAmount;
  maxErrorMessage?: string;
}

// Copied from TokenInputField but removed token selection and usd amount
export default function TokenInputAmount({
  token,
  value,
  onChange,
  balance,
  label,
  error,
  showError = true,
  disabled = false,
  disableMaxButton = false,
  focusOnMount = false,
  acceptOverflowAmount = false,
  useBalanceAsMaxButton = false,
  isBalanceLoading,
  setError,
  onClickMax,
  balanceLabel,
  maxUpperLimit,
  maxErrorMessage = 'Amount exceeds available balance',
}: TokenInputAmountProps): React.JSX.Element {
  const [maxAmountError, setMaxAmountError] = useState<string>();
  const displayedError = showError ? (error ?? maxAmountError) : undefined;

  const inputRef = useRef<HTMLInputElement>(null);
  const {
    onChange: onInputChange,
    value: inputValue,
    setValue: setInputValue,
  } = useValidatedTokenAmountInput({
    token,
    initialValue: value?.toFixed() ?? '',
  });
  const validatedAmount = useValidatedTokenAmount(inputValue, token);
  const previousValidatedAmountRef = useRef<TokenAmount | undefined>(undefined);
  const previousBalance = usePrevious(balance);

  const handleMaxInput = () => {
    const maxAmount = maxUpperLimit && balance?.gt(maxUpperLimit) ? maxUpperLimit : balance;
    setInputValue(maxAmount?.toFixed() ?? '');
    onClickMax?.();
  };
  const maxButtonDisabled =
    disableMaxButton || disabled || !balance || !balance.gt(0) || balance.eq(validatedAmount);

  const balanceLabelContent = token && balance && (
    <>
      {balanceLabel ?? 'Balance'}:{' '}
      {isBalanceLoading ? (
        <SkeletonLine width={50} />
      ) : (
        <span className="whitespace-nowrap font-aeonikMedium text-cf-white">
          {balance.toFormatted()} {token.symbol}
        </span>
      )}
    </>
  );

  useEffect(() => {
    const balanceChanged = !balance?.eq(previousBalance);

    if (balance && validatedAmount?.gt(balance)) {
      if (acceptOverflowAmount) {
        onChange(validatedAmount);
      } else {
        onChange(undefined);
      }
      setMaxAmountError(maxErrorMessage);
      setError?.(maxErrorMessage);
    } else {
      // Clear errors if validatedAmount changed or balance changed (e.g., finished loading)
      if (
        !validatedAmount ||
        !validatedAmount.eq(previousValidatedAmountRef.current) ||
        balanceChanged
      ) {
        setError?.('');
        setMaxAmountError(undefined);
      }
      onChange(validatedAmount);
    }
    previousValidatedAmountRef.current = validatedAmount;
  }, [validatedAmount, balance, acceptOverflowAmount, previousBalance]);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, []);

  return (
    <div className="w-full">
      <div
        className={classNames(
          'relative flex h-[55px] flex-row text-cf-light-4',
          disabled && 'cursor-not-allowed [&>*]:pointer-events-none',
        )}
      >
        <div
          className={classNames(
            'relative grow rounded-md border border-cf-gray-4 bg-cf-gray-3-5 shadow-grayPop3 transition',
            displayedError && '!border-cf-red-1',
            !disabled && 'group transition hover:border-cf-gray-5 hover:bg-cf-gray-4', // apply group-hover effect only when enabled
          )}
        >
          <div className="absolute left-2 top-[-10px] z-10 select-none px-[6px] text-12 text-cf-light-2">
            <div>{label}</div>
            <div className="absolute inset-x-0 bottom-0 top-[9px] -z-10 bg-cf-gray-3-5 transition group-hover:bg-cf-gray-4" />
          </div>
          <button
            className={classNames(
              'absolute right-3 top-1/2 -translate-y-1/2 rounded-md border border-cf-gray-5 bg-cf-gray-4 px-1.5 py-0.5 text-10 opacity-0 transition duration-100 ease-out',
              'z-10 hover:border-cf-light-1 hover:bg-cf-gray-5 disabled:pointer-events-none enabled:group-focus-within:opacity-100 enabled:group-hover:opacity-100',
            )}
            onClick={handleMaxInput}
            type="button"
            disabled={maxButtonDisabled}
          >
            MAX
          </button>
          <input
            placeholder="0"
            type="text"
            inputMode="decimal"
            className={classNames(
              'peer h-full w-full bg-transparent pl-3 pr-2 font-aeonikMedium text-20 text-white outline-none transition placeholder:text-cf-light-2 disabled:text-cf-light-2',
            )}
            value={inputValue}
            onChange={onInputChange}
            onFocus={() =>
              // Set the value to the raw string value (unformatted/without commas) when the input is focused
              onInputChange({
                target: { value: inputValue },
              } as ChangeEvent<HTMLInputElement>)
            }
            onBlur={() => {
              if (validatedAmount) {
                setInputValue(validatedAmount.eq(0) ? '' : validatedAmount.toFixed());
              }
            }}
            disabled={disabled}
            ref={inputRef}
          />
        </div>
      </div>
      <div className="flex flex-row items-center justify-between pt-2">
        <div className="text-12 text-cf-red-1">
          {displayedError}
          &nbsp;{/* prevent layout jump when error appears */}
        </div>
        {balanceLabelContent && (
          <div className="text-right text-12 text-cf-light-2">
            {useBalanceAsMaxButton ? (
              <button type="button" disabled={maxButtonDisabled} onClick={handleMaxInput}>
                {balanceLabelContent}
              </button>
            ) : (
              balanceLabelContent
            )}
          </div>
        )}
      </div>
    </div>
  );
}
